/**
 * Runtime normalization of meeting transcription results.
 *
 * Version 2 results carry structured `transcript_turns`, `analysis`,
 * `speaker_diagnostics`, `speaker_anchors`, and `artifacts`. Legacy rows only
 * have `transcript` / `transcription`, `summary`, and `actions` /
 * `action_items`. This module normalizes both into one shape and enforces the
 * documented precedence rules so components never guess:
 *
 * - Timestamps are seconds (floats).
 * - Identity similarity is a 0..1 range.
 * - Transcript precedence: structured turns, then `transcript`, then
 *   `transcription`.
 * - Legacy `actions` and `action_items` are normalized once and deduplicated.
 */

export const RESULT_CONTRACT_VERSION = 2;

/**
 * @typedef {Object} TranscriptTurn
 * @property {string|null} id
 * @property {string|null} speakerId
 * @property {string|null} speakerName
 * @property {number|null} startSeconds
 * @property {number|null} endSeconds
 * @property {string} text
 * @property {number|null} confidence 0..1
 * @property {{flagged: boolean, reason: string|null}} review
 */

/**
 * @typedef {Object} AnalysisItem
 * @property {string} task
 * @property {string|null} assignee
 * @property {string|null} dueDate
 * @property {string|null} status
 */

/**
 * @typedef {Object} MeetingAnalysis
 * @property {string|null} title
 * @property {string|null} summary
 * @property {string[]} decisions
 * @property {string[]} nextSteps
 * @property {string[]} openQuestions
 * @property {AnalysisItem[]} actionItems
 */

/**
 * @typedef {Object} SpeakerDiagnostic
 * @property {string|null} speakerId
 * @property {string|null} speakerName
 * @property {number|null} turnCount
 * @property {number|null} totalSeconds
 * @property {number|null} talkRatio
 */

/**
 * @typedef {Object} SpeakerAnchor
 * @property {string|null} speakerId
 * @property {string|null} displayName
 * @property {number|null} identitySimilarity 0..1
 * @property {string[]} referenceEmbeddings
 */

/**
 * @typedef {Object} ArtifactDescriptor
 * @property {string|null} id
 * @property {string} name
 * @property {string|null} type
 * @property {string|null} objectKey
 * @property {number|null} sizeBytes
 * @property {string|null} mimeType
 * @property {string|null} createdAt
 */

/**
 * @typedef {Object} NormalizedMeetingResult
 * @property {number|null} contractVersion
 * @property {boolean} isLegacy
 * @property {TranscriptTurn[]} turns
 * @property {MeetingAnalysis} analysis
 * @property {SpeakerDiagnostic[]} diagnostics
 * @property {SpeakerAnchor[]} anchors
 * @property {ArtifactDescriptor[]} artifacts
 * @property {string[]} actionItems Deduplicated, normalized action items.
 * @property {string|null} effectiveTranscript
 * @property {string|null} updatedAt
 */

const clamp = (value, min, max) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.min(max, Math.max(min, number));
};

const toSeconds = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

const toStringOrNull = (value) => (typeof value === 'string' && value ? value : null);

const toStringArray = (value) =>
  Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];

/**
 * Normalize a single raw transcript turn.
 * @param {*} raw
 * @returns {TranscriptTurn}
 */
export const normalizeTurn = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return {
      id: null,
      speakerId: null,
      speakerName: null,
      startSeconds: null,
      endSeconds: null,
      text: '',
      confidence: null,
      review: { flagged: false, reason: null }
    };
  }
  const startSeconds = toSeconds(raw.start_seconds ?? raw.startSeconds);
  const endSeconds = toSeconds(raw.end_seconds ?? raw.endSeconds);
  return {
    id: toStringOrNull(raw.id),
    speakerId: toStringOrNull(raw.speaker_id ?? raw.speakerId ?? raw.speaker_cluster),
    speakerName: toStringOrNull(raw.speaker_name ?? raw.speakerName ?? raw.speaker),
    startSeconds,
    endSeconds,
    text: typeof raw.text === 'string' ? raw.text : '',
    confidence: clamp(raw.confidence ?? raw.confidenceScore, 0, 1),
    review: raw.review && typeof raw.review === 'object'
      ? {
          flagged: raw.review.flagged === true,
          reason: typeof raw.review.reason === 'string' ? raw.review.reason : null
        }
      : { flagged: raw.needs_review === true, reason: null }
  };
};

/**
 * Normalize the structured analysis object.
 * @param {*} raw
 * @returns {MeetingAnalysis}
 */
export const normalizeAnalysis = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return {
      title: null,
      summary: null,
      decisions: [],
      nextSteps: [],
      openQuestions: [],
      actionItems: []
    };
  }
  const rawActionItems = Array.isArray(raw.action_items) ? raw.action_items : [];
  const actionItems = rawActionItems
    .map((item) => {
      if (typeof item === 'string') {
        return { task: item, assignee: null, dueDate: null, status: null };
      }
      if (item && typeof item === 'object' && typeof item.task === 'string') {
        return {
          task: item.task,
          assignee: toStringOrNull(item.assignee),
          dueDate: toStringOrNull(item.due_date ?? item.dueDate),
          status: toStringOrNull(item.status)
        };
      }
      return null;
    })
    .filter(Boolean);
  return {
    title: toStringOrNull(raw.title),
    summary: toStringOrNull(raw.summary),
    decisions: toStringArray(raw.decisions),
    nextSteps: toStringArray(raw.next_steps ?? raw.nextSteps),
    openQuestions: toStringArray(raw.open_questions ?? raw.openQuestions),
    actionItems
  };
};

/**
 * Normalize speaker diagnostics.
 * @param {*} raw
 * @returns {SpeakerDiagnostic[]}
 */
export const normalizeDiagnostics = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => ({
      speakerId: toStringOrNull(entry.speaker_id ?? entry.speakerId),
      speakerName: toStringOrNull(entry.speaker_name ?? entry.speakerName),
      turnCount:
        Number.isInteger(entry.turn_count ?? entry.turnCount) &&
        (entry.turn_count ?? entry.turnCount) >= 0
          ? entry.turn_count ?? entry.turnCount
          : null,
      totalSeconds: toSeconds(entry.total_seconds ?? entry.totalSeconds),
      talkRatio: clamp(entry.talk_ratio ?? entry.talkRatio, 0, 1)
    }));
};

/**
 * Normalize speaker anchors.
 * @param {*} raw
 * @returns {SpeakerAnchor[]}
 */
export const normalizeAnchors = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => ({
      speakerId: toStringOrNull(entry.speaker_id ?? entry.speakerId),
      displayName: toStringOrNull(entry.display_name ?? entry.displayName),
      identitySimilarity: clamp(entry.identity_similarity ?? entry.identitySimilarity, 0, 1),
      referenceEmbeddings: toStringArray(entry.reference_embeddings ?? entry.referenceEmbeddings)
    }));
};

/**
 * Normalize the artifact manifest.
 * @param {*} raw
 * @returns {ArtifactDescriptor[]}
 */
export const normalizeArtifacts = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => ({
      id: toStringOrNull(entry.id),
      name: typeof entry.name === 'string' ? entry.name : '',
      type: toStringOrNull(entry.type),
      objectKey: toStringOrNull(entry.object_key ?? entry.objectKey),
      sizeBytes:
        Number.isInteger(entry.size_bytes ?? entry.sizeBytes) &&
        (entry.size_bytes ?? entry.sizeBytes) >= 0
          ? entry.size_bytes ?? entry.sizeBytes
          : null,
      mimeType: toStringOrNull(entry.mime_type ?? entry.mimeType),
      createdAt: toStringOrNull(entry.created_at ?? entry.createdAt)
    }));
};

/**
 * Normalize legacy `actions` and `action_items` into a single array of strings.
 * Accepts strings (possibly JSON), arrays of strings, or arrays of `{task}`.
 * @param {*} actions
 * @param {*} actionItems
 * @returns {string[]}
 */
export const normalizeLegacyActions = (actions, actionItems) => {
  const normalized = [];
  const pushItem = (item) => {
    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (trimmed) normalized.push(trimmed);
    } else if (item && typeof item === 'object' && typeof item.task === 'string') {
      const trimmed = item.task.trim();
      if (trimmed) normalized.push(trimmed);
    }
  };

  const parseValue = (value) => {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // Not JSON: treat as a single plain-text item.
      }
      return [value];
    }
    return [];
  };

  parseValue(actions).forEach(pushItem);
  parseValue(actionItems).forEach(pushItem);
  return [...new Set(normalized)];
};

/**
 * Resolve the effective transcript text using documented precedence:
 * structured turns, then `transcript`, then `transcription`.
 * @param {TranscriptTurn[]} turns
 * @param {*} meeting
 * @returns {string|null}
 */
export const getEffectiveTranscript = (turns, meeting) => {
  if (turns && turns.length > 0) {
    return turns.map((turn) => turn.text).filter(Boolean).join('\n');
  }
  if (typeof meeting?.transcript === 'string' && meeting.transcript.trim()) {
    return meeting.transcript;
  }
  if (typeof meeting?.transcription === 'string' && meeting.transcription.trim()) {
    return meeting.transcription;
  }
  return null;
};

/**
 * Normalize a raw meeting row (or detail DTO) into a stable result shape.
 * @param {Object} meeting
 * @returns {NormalizedMeetingResult}
 */
export const normalizeMeetingResult = (meeting) => {
  if (!meeting || typeof meeting !== 'object') {
    return {
      contractVersion: null,
      isLegacy: true,
      turns: [],
      analysis: normalizeAnalysis(null),
      diagnostics: [],
      anchors: [],
      artifacts: [],
      actionItems: [],
      effectiveTranscript: null,
      updatedAt: null
    };
  }

  const rawTurns = meeting.transcript_turns ?? meeting.transcriptTurns ?? [];
  const contractVersion =
    meeting.result_contract_version ?? meeting.resultContractVersion ?? null;
  const turns = Array.isArray(rawTurns) ? rawTurns.map(normalizeTurn) : [];
  const hasStructuredTurns = turns.length > 0 || Number(contractVersion) === RESULT_CONTRACT_VERSION;

  const analysis = hasStructuredTurns
    ? normalizeAnalysis(meeting.analysis)
    : normalizeAnalysis(null);

  // Legacy action normalization is always applied and deduplicated once.
  const actionItems = normalizeLegacyActions(
    meeting.actions,
    meeting.action_items
  );

  // When structured analysis carries action items, prefer those but merge any
  // legacy-only items so nothing is silently dropped.
  const structuredActionItems = analysis.actionItems.map((item) => item.task);
  const mergedActionItems = [...new Set([...actionItems, ...structuredActionItems])];

  return {
    contractVersion: Number.isInteger(contractVersion) ? contractVersion : null,
    isLegacy: !hasStructuredTurns,
    turns,
    analysis: hasStructuredTurns
      ? { ...analysis, actionItems: analysis.actionItems }
      : analysis,
    diagnostics: hasStructuredTurns
      ? normalizeDiagnostics(meeting.speaker_diagnostics ?? meeting.speakerDiagnostics)
      : [],
    anchors: hasStructuredTurns
      ? normalizeAnchors(meeting.speaker_anchors ?? meeting.speakerAnchors)
      : [],
    artifacts: hasStructuredTurns ? normalizeArtifacts(meeting.artifacts) : [],
    actionItems: mergedActionItems,
    effectiveTranscript: getEffectiveTranscript(turns, meeting),
    updatedAt: toStringOrNull(meeting.result_updated_at ?? meeting.resultUpdatedAt)
  };
};

/**
 * Format seconds (integer) as `m:ss`.
 * @param {number|null} seconds
 * @returns {string}
 */
export const formatTimestamp = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === '') return '--:--';
  if (!Number.isFinite(Number(seconds)) || Number(seconds) < 0) return '--:--';
  const totalSeconds = Math.floor(Number(seconds));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};
